import { useState, useEffect } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    Alert,
    CircularProgress,
    Grid,
    IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LinkIcon from "@mui/icons-material/Link";
import { useTranslation, TFunction } from "react-i18next";
import { useQuery } from '@tanstack/react-query';
import { CarSharingSubscription } from "../../types";
import { getActiveSubscriptions, getHistorySubscriptions } from "../../api/service/customerbaseApi";
import { GridColDef } from "@mui/x-data-grid";
import CarSharingDataGrid from "./CarSharingDataGrid";
import { CarsharingRedirectParams, redirectToCarsharing } from '../../../../util/carsharingService';
import { useCarSharingContext } from './CarSharingContext';

const dateValueFormatter = ({ value }: { value: string | undefined | null }) => {
    if (!value) return '-';

    const date = new Date(value);
    if (isNaN(date.getTime())) return '-';

    return date.toLocaleString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'Europe/Rome',
    }).replace(',', '');
};


const getActiveColumns = (t: TFunction): GridColDef[] => [
    {
        field: 'description',
        headerName: t('csSubscriptions.headers.description'),
        flex: 1,
        minWidth: 170
    },
    {
        field: 'monthlyUsedMinutes',
        headerName: t('csSubscriptions.headers.monthlyMinutesUsed'),
        flex: 1,
        minWidth: 170
    },
    {
        field: 'monthlyUsedMinutesDef',
        headerName: t('csSubscriptions.headers.monthlyMinutesRemaining'),
        flex: 1,
        minWidth: 170
    },
    {
        field: 'monthlyTotal',
        headerName: t('csSubscriptions.headers.monthlyMinutesTotal'),
        flex: 1,
        minWidth: 200,
        valueGetter: (params) => (params.row.monthlyUsedMinutes || 0) + (params.row.monthlyUsedMinutesDef || 0),
    },
    {
        field: 'activationDate',
        headerName: t('csSubscriptions.headers.activationDate'),
        flex: 1,
        minWidth: 160,
        valueFormatter: dateValueFormatter,
    },
    {
        field: 'nextMonthlyRenew',
        headerName: t('csSubscriptions.headers.monthlyExpiryDate'),
        flex: 1,
        minWidth: 160,
        valueFormatter: dateValueFormatter,
    },
    {
        field: 'expiryDateSubscription',
        headerName: t('csSubscriptions.headers.annualExpiryDate'),
        flex: 1,
        minWidth: 160,
        valueFormatter: dateValueFormatter,
    },
    {
        field: 'renewIndex',
        headerName: t('csSubscriptions.headers.renewals'),
        flex: 1,
        minWidth: 170
    },
];

const getHistoryColumns = (t: TFunction): GridColDef[] => [
    {
        field: 'description',
        headerName: t('csSubscriptions.headers.description'),
        flex: 1,
        minWidth: 170,
    },
    {
        field: 'monthlyUsedMinutes',
        headerName: t('csSubscriptions.headers.monthlyMinutesUsed'),
        flex: 1,
        minWidth: 170,
    },
    {
        field: 'monthlyUsedMinutesDef',
        headerName: t('csSubscriptions.headers.monthlyMinutesTotal'),
        flex: 1,
        minWidth: 200,
    },
    {
        field: 'activationDate',
        headerName: t('csSubscriptions.headers.activationDate'),
        flex: 1,
        minWidth: 160,
        valueFormatter: dateValueFormatter,
    },
    {
        field: 'expiringDate',
        headerName: t('csSubscriptions.headers.expiryDate'),
        flex: 1,
        minWidth: 160,
        valueFormatter: dateValueFormatter,
    },
    {
        field: 'renewIndex',
        headerName: t('csSubscriptions.headers.renewals'),
        flex: 1,
        minWidth: 170,
    },
];

export default function CustomerBaseCarSharingSubscriptions() {
    const { t } = useTranslation("customerbase");
    const { personId, csCustomerId, language } = useCarSharingContext();

    const [expanded, setExpanded] = useState(false);
    const [hasBeenOpened, setHasBeenOpened] = useState(false);

    useEffect(() => {
        if (expanded && !hasBeenOpened) setHasBeenOpened(true);
    }, [expanded, hasBeenOpened]);

    const handleRedirect = async () => {
        if (!csCustomerId) return;

        const params: CarsharingRedirectParams = {
            lang: language || 'it',
            type: 'CUSTOMER',
            section: 'SUBSCRIPTIONS',
            id: String(csCustomerId),
        };

        try {
            await redirectToCarsharing(params);
        } catch (error) {
            console.error('Redirect failed:', error);
        }
    };

    const {
        data: activeSubscriptions,
        isError: isErrorActive,
        error: errorActive,
        isLoading: isLoadingActive,
    } = useQuery<CarSharingSubscription[]>(
        ['activeSubscriptions', personId, csCustomerId, language],
        () => getActiveSubscriptions(String(csCustomerId!), personId!),
        {
            enabled: hasBeenOpened && !!csCustomerId && !!personId,
        }
    );

    const {
        data: historySubscriptions,
        isError: isErrorHistory,
        error: errorHistory,
        isLoading: isLoadingHistory,
    } = useQuery<CarSharingSubscription[]>(
        ['historySubscriptions', personId, csCustomerId, language],
        () => getHistorySubscriptions(String(csCustomerId!), personId!),
        {
            enabled: hasBeenOpened && !!csCustomerId && !!personId,
        }
    );

    const activeColumns = getActiveColumns(t);
    const historyColumns = getHistoryColumns(t);

    return (
        <Accordion expanded={expanded} onChange={(_, isExpanded) => setExpanded(isExpanded)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <Typography sx={{ fontWeight: 500 }}>{t("tabs.accordionsLabels.csSubscriptions")}</Typography>
                    {expanded && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                void handleRedirect();
                            }}
                            disabled={!csCustomerId}
                            color="secondary"
                        >
                            <LinkIcon />
                        </IconButton>
                    )}
                </Box>
            </AccordionSummary>

            <AccordionDetails>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        {isLoadingActive ? (
                            <CircularProgress />
                        ) : isErrorActive ? (
                            <Alert severity="error">{(errorActive as Error)?.message}</Alert>
                        ) : (
                            <CarSharingDataGrid
                                title={t('csSubscriptions.active')}
                                rows={activeSubscriptions || []}
                                columns={activeColumns}
                            />
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        {isLoadingHistory ? (
                            <CircularProgress />
                        ) : isErrorHistory ? (
                            <Alert severity="error">{(errorHistory as Error)?.message}</Alert>
                        ) : (
                            <CarSharingDataGrid
                                title={t('csSubscriptions.history')}
                                rows={historySubscriptions || []}
                                columns={historyColumns}
                            />
                        )}
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}
